import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useConfig } from '../../services/api/hooks/useConfig';
import { useSubscribers } from '../../services/api/hooks/useSubscribers';
import { clearConfigCache, getMissingConfigs } from '../../services/config';
import { useAuth } from '../../contexts/AuthContext';
import MailServerControl from './MailServerControl';
import DatabaseTest from './DatabaseTest';

interface ConfigManagerProps {
  onConfigComplete?: () => void;
  showOnlyRequired?: boolean;
}

const ConfigManager: React.FC<ConfigManagerProps> = ({ onConfigComplete, showOnlyRequired = false }) => {
  const { user: currentUser } = useAuth();
  const [config, setConfig] = useState<any>({});
  const [message, setMessage] = useState('');
  const [missingConfigs, setMissingConfigs] = useState<string[]>([]);
  const { data: configData, loading, updateConfig: updateConfigHook } = useConfig();
  const { recalculateSnapshots } = useSubscribers();
  const [snapshotStartDate, setSnapshotStartDate] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalculateMessage, setRecalculateMessage] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      setConfig(configData);
      const missing = await getMissingConfigs();
      setMissingConfigs(missing);
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  }, [configData]);

  useEffect(() => {
    loadConfig();
  }, [configData, loadConfig]);

  if (!currentUser?.isAdmin) {
    return (
      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#ffcdd2' }}>Access Denied</Card.Header>
        <Card.Body>
          <Alert variant="warning">
            You must be an administrator to access system settings.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const handleUpdate = async (key: string, value: string) => {
    // Find field configuration
    const field = Object.values(configSections).flat().find(f => f.key === key);

    // Prevent blank values for required fields
    if (field?.required && (!value || value.trim() === '')) {
      setMessage(`${field.label} cannot be empty`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      await updateConfigHook({ [key]: value });
      setMessage(`${key} updated successfully`);
      setConfig({ ...config, [key]: value });
      await clearConfigCache();

      const missing = await getMissingConfigs();
      setMissingConfigs(missing);

      if (missing.length === 0 && onConfigComplete) {
        onConfigComplete();
      }
    } catch (error) {
      setMessage(`Failed to update ${key}`);
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleRecalculateSnapshots = async () => {
    if (!snapshotStartDate) {
      setRecalculateMessage('Please select a start date');
      setTimeout(() => setRecalculateMessage(''), 3000);
      return;
    }

    const confirmed = window.confirm(
      `This will recalculate all daily subscriber snapshots from ${snapshotStartDate} to today. This may take several minutes. Continue?`
    );

    if (!confirmed) return;

    setRecalculating(true);
    setRecalculateMessage('');

    try {
      const data = await recalculateSnapshots(snapshotStartDate) as any;
      setRecalculateMessage(`✓ ${data.message}`);
      setSnapshotStartDate('');
    } catch (error) {
      console.error('Error recalculating snapshots:', error);
      setRecalculateMessage('✗ Failed to recalculate snapshots. Please try again.');
    } finally {
      setRecalculating(false);
      setTimeout(() => setRecalculateMessage(''), 10000);
    }
  };

  const configSections = {
    'Authentication & Security': [
      { 
        key: 'auth_token', 
        label: 'Auth Token', 
        type: 'password', 
        required: true,
        description: 'Authentication token for API access'
      },
      { 
        key: 'jwt_secret', 
        label: 'JWT Secret', 
        type: 'password', 
        required: true,
        description: 'Secret key for JWT token signing and verification'
      }
    ],
    'Backend Service': [
      { 
        key: 'backend_api_url', 
        label: 'Backend API URL', 
        type: 'url', 
        required: true,
        description: 'Main backend service endpoint for campaign management and subscriber operations'
      }
    ],
    'Email & Tracking Service': [
      { 
        key: 'tracking_url', 
        label: 'Tracking URL', 
        type: 'url', 
        required: true,
        description: 'Domain used for email open tracking pixels and click tracking'
      },
      { 
        key: 'default_sender_email', 
        label: 'Default Sender Email', 
        type: 'email', 
        required: true,
        description: 'Default "From" email address for outgoing campaigns'
      },
      { 
        key: 'default_sender_name', 
        label: 'Default Sender Name', 
        type: 'text', 
        required: true,
        description: 'Default "From" name displayed in email clients'
      },
      { 
        key: 'unsubscribe_worker_url', 
        label: 'Unsubscribe Worker URL', 
        type: 'url', 
        required: true,
        description: 'Cloudflare Worker endpoint handling unsubscribe requests'
      }
    ],
    'AWS Services': [
      { 
        key: 'aws_region', 
        label: 'AWS Region', 
        type: 'text', 
        required: true,
        description: 'AWS region for SES email sending and SQS queue operations (e.g., us-east-1)'
      },
      { 
        key: 'aws_access_key_id', 
        label: 'AWS Access Key ID', 
        type: 'password', 
        required: true,
        description: 'AWS access key ID for SES and SQS operations'
      },
      { 
        key: 'aws_secret_access_key', 
        label: 'AWS Secret Access Key', 
        type: 'password', 
        required: true,
        description: 'AWS secret access key for SES and SQS operations'
      },
      { 
        key: 'sqs_queue_url', 
        label: 'SQS Queue URL', 
        type: 'url', 
        required: true,
        description: 'Amazon SQS queue URL for email processing'
      },
      { 
        key: 'dynamodb_table_name', 
        label: 'DynamoDB Table Name', 
        type: 'text', 
        required: true,
        description: 'DynamoDB table name for storing email tracking data'
      }
    ],
    'Email Validation Service': [
      { 
        key: 'debounce_api_key', 
        label: 'Debounce API Key', 
        type: 'password', 
        required: true,
        description: 'API key for Debounce.io email validation service'
      },
      { 
        key: 'debounce_enabled', 
        label: 'Enable Debounce Processing', 
        type: 'checkbox', 
        required: false,
        description: 'Enable or disable automatic email validation processing via Debounce.io'
      },
      { 
        key: 'debounce_invalid_codes', 
        label: 'Debounce Invalid Codes', 
        type: 'text', 
        required: false,
        description: 'Comma-separated list of Debounce result codes that mark emails as invalid (default: 1,2,3,6,7)'
      }
    ],
    'KumoMTA Service': [
      { 
        key: 'kumomta_metrics_url', 
        label: 'KumoMTA Metrics URL', 
        type: 'url', 
        required: true,
        description: 'KumoMTA metrics endpoint for email delivery statistics'
      },
      { 
        key: 'bearer_token', 
        label: 'Bearer Token', 
        type: 'password', 
        required: true,
        description: 'Authentication token for KumoMTA metrics API access'
      }
    ],
    'Monitoring Service': [
      { 
        key: 'grafana_url', 
        label: 'Grafana Dashboard URL', 
        type: 'url', 
        required: true,
        description: 'Grafana dashboard URL for system monitoring and analytics'
      }
    ],
    'File Storage Service': [
      {
        key: 'r2_domain',
        label: 'R2 Domain',
        type: 'url',
        required: true,
        description: 'Cloudflare R2 bucket domain for bulk email validation file storage'
      },
      {
        key: 'r2_images_domain',
        label: 'R2 Images Domain',
        type: 'url',
        required: true,
        description: 'Cloudflare R2 bucket domain for media/image uploads'
      }
    ],
    'Campaign Processing': [
      { 
        key: 'max_parallel_workflows', 
        label: 'Max Parallel Workflows', 
        type: 'number', 
        required: false,
        description: 'Maximum number of parallel workflows for campaign processing (default: 990)'
      },
      { 
        key: 'batch_size', 
        label: 'Batch Size', 
        type: 'number', 
        required: false,
        description: 'Number of emails processed per workflow batch (default: 9900)'
      },
      { 
        key: 'workflow_creation_delay', 
        label: 'Workflow Creation Delay (ms)', 
        type: 'number', 
        required: false,
        description: 'Delay between workflow creations to avoid rate limits (default: 100ms)'
      }
    ],
    'Auto Opt-Out Configuration': [
      { 
        key: 'auto_optout_res_codes', 
        label: 'Auto Opt-Out Response Codes', 
        type: 'text', 
        required: false,
        description: 'Comma-separated response codes that trigger auto opt-out (e.g., 550,551,553). Leave empty to disable.'
      },
      { 
        key: 'platform_disable_url', 
        label: 'Platform Disable URL', 
        type: 'url', 
        required: false,
        description: 'External API endpoint to disable opted-out emails on platform (use {{emails}} placeholder for email list)'
      }
    ],
    'Time Filter Configuration': [
      { 
        key: 'default_time_filter', 
        label: 'Default Time Filter', 
        type: 'text', 
        required: false,
        description: 'Default time filter for data display (e.g., -1 month, -3 months, -1 year, or leave empty for all data)'
      }
    ],
    'CSV Processing Configuration': [
      { 
        key: 'csv_chunk_size', 
        label: 'CSV Upload Chunk Size', 
        type: 'number', 
        required: false,
        description: 'Number of rows per chunk when uploading large CSV files (default: 5000)'
      },
      { 
        key: 'csv_export_chunk_size', 
        label: 'CSV Export Chunk Size', 
        type: 'number', 
        required: false,
        description: 'Number of rows per page when exporting CSV files (default: 200000)'
      }
    ],
    'Mail Server Management': [
      {
        key: 'mail_server_management_url',
        label: 'Mail Server Management URL',
        type: 'url',
        required: false,
        description: 'AWS Lambda endpoint for mail server status and instance type management'
      }
    ]
  };



  return (
    <Card className="m-3">
      <Card.Header as="h5">
        {showOnlyRequired ? 'Required Configuration Setup' : 'Mailing System Configuration'}
      </Card.Header>
      <Card.Body>
        {showOnlyRequired && missingConfigs.length > 0 && (
          <Alert variant="warning">
            <strong>Setup Required:</strong> Please configure all required settings to access the application.
            <br />Missing: {missingConfigs.join(', ')}
          </Alert>
        )}
        
        {message && <Alert variant="info">{message}</Alert>}
        
        {showOnlyRequired ? (
          Object.entries(configSections).map(([sectionName, fields]) => {
            const sectionFields = fields.filter(f => f.required);
            if (sectionFields.length === 0) return null;
            return (
              <div key={sectionName} className="mb-4">
                <h6 className="text-primary border-bottom pb-2">{sectionName}</h6>
                {sectionFields.map(field => (
                  <Form.Group key={field.key} className="mb-3">
                    <Form.Label>
                      {field.label}
                      {field.required && <span className="text-danger"> *</span>}
                    </Form.Label>
                    {field.type === 'checkbox' ? (
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="checkbox"
                          checked={config[field.key] === 'true'}
                          onChange={(e) => {
                            const newValue = e.target.checked ? 'true' : 'false';
                            setConfig({ ...config, [field.key]: newValue });
                            handleUpdate(field.key, newValue);
                          }}
                          disabled={loading}
                        />
                        <span>{config[field.key] === 'true' ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    ) : (
                      <InputGroup>
                        <Form.Control
                          type={field.type}
                          value={config[field.key] || ''}
                          onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                          onBlur={(e) => {
                            if (e.target.value !== config[field.key]) {
                              handleUpdate(field.key, e.target.value);
                            }
                          }}
                          disabled={loading}
                          className={missingConfigs.includes(field.key) ? 'border-danger' : ''}
                          autoComplete="off"
                        />
                        <Button 
                          variant="outline-primary" 
                          onClick={() => handleUpdate(field.key, config[field.key] || '')}
                          disabled={loading || (field.required && (!config[field.key] || config[field.key].trim() === ''))}
                        >
                          Save
                        </Button>
                      </InputGroup>
                    )}
                    <Form.Text className="text-muted">
                      {field.description}
                    </Form.Text>
                  </Form.Group>
                ))}
              </div>
            );
          })
        ) : (
          Object.entries(configSections).map(([sectionName, fields]) => (
            <div key={sectionName} className="mb-4">
              <h6 className="text-primary border-bottom pb-2">{sectionName}</h6>
              {fields.map(field => (
                <Form.Group key={field.key} className="mb-3">
                  <Form.Label>
                    {field.label}
                    {field.required && <span className="text-danger"> *</span>}
                  </Form.Label>
                  {field.type === 'checkbox' ? (
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        checked={config[field.key] === 'true'}
                        onChange={(e) => {
                          const newValue = e.target.checked ? 'true' : 'false';
                          setConfig({ ...config, [field.key]: newValue });
                          handleUpdate(field.key, newValue);
                        }}
                        disabled={loading}
                      />
                      <span>{config[field.key] === 'true' ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  ) : (
                    <InputGroup>
                      <Form.Control
                        type={field.type}
                        value={config[field.key] || ''}
                        onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                        onBlur={(e) => {
                          if (e.target.value !== config[field.key]) {
                            handleUpdate(field.key, e.target.value);
                          }
                        }}
                        disabled={loading}
                        className={missingConfigs.includes(field.key) ? 'border-danger' : ''}
                        autoComplete="off"
                      />
                      <Button 
                        variant="outline-primary" 
                        onClick={() => handleUpdate(field.key, config[field.key] || '')}
                        disabled={loading || (field.required && (!config[field.key] || config[field.key].trim() === ''))}
                      >
                        Save
                      </Button>
                    </InputGroup>
                  )}
                  <Form.Text className="text-muted">
                    {field.description}
                  </Form.Text>
                </Form.Group>
              ))}
            </div>
          ))
        )}

        {!showOnlyRequired && (
          <>
            <MailServerControl />
            
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2">Subscriber Analytics Maintenance</h6>
              <Form.Group className="mb-3">
                <Form.Label>Recalculate Daily Snapshots</Form.Label>
                <Form.Text className="text-muted d-block mb-2">
                  Use this tool to recalculate subscriber snapshots when you upload historical data.
                  Enter the earliest date you want to recalculate from, and all snapshots from that date to today will be regenerated.
                </Form.Text>
                <InputGroup>
                  <Form.Control
                    type="date"
                    value={snapshotStartDate}
                    onChange={(e) => setSnapshotStartDate(e.target.value)}
                    disabled={recalculating}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <Button
                    variant="warning"
                    onClick={handleRecalculateSnapshots}
                    disabled={recalculating || !snapshotStartDate}
                  >
                    {recalculating ? 'Recalculating...' : 'Recalculate Snapshots'}
                  </Button>
                </InputGroup>
                {recalculateMessage && (
                  <Alert
                    variant={recalculateMessage.startsWith('✓') ? 'success' : 'danger'}
                    className="mt-2 mb-0"
                  >
                    {recalculateMessage}
                  </Alert>
                )}
              </Form.Group>
            </div>

            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2">Database Connection Test</h6>
              <DatabaseTest />
            </div>

            <Button onClick={async () => { await clearConfigCache(); loadConfig(); }} disabled={loading}>
              Refresh Configuration
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ConfigManager;